import { Button } from '@shared/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLinkItem,
  DropdownMenuTrigger,
} from '@shared/components/ui/dropdown-menu';
import { RenderItemProps } from '@shared/components/ui/VirtualList';
import { Eye, MoreVertical, X } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Entry, ENTRY_STATUS_COLORS, EntryStatus, getEntryStatusLabel } from '../../../models';

const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '—';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  } catch {
    return '—';
  }
};

export interface EntryCardProps extends RenderItemProps<Entry> {
  isTenant3pl?: boolean;
  onCancelEntry?: (entry: Entry) => void;
  canCancel?: boolean;
}

export const EntryCard: React.FC<EntryCardProps> = ({
  item: entry,
  isTenant3pl = false,
  onCancelEntry,
  canCancel = false,
}) => {
  const { t } = useTranslation('merchandiseV3');

  const status = entry.status as EntryStatus;
  const statusLabel = getEntryStatusLabel(status, t);
  const statusColor = ENTRY_STATUS_COLORS[status] || 'bg-muted text-muted-foreground';

  return (
    <article
      data-testid={`entry-card-${entry.id}`}
      className="touch-manipulation rounded-lg border border-border bg-card p-4 shadow-sm"
    >
      <div className="flex items-start gap-2">
        <div className="flex min-w-0 flex-1 flex-col gap-3">
          <div className="space-y-2">
            <h3 className="truncate text-base font-semibold text-foreground">{entry.reference}</h3>
            <span
              className={`inline-flex max-w-full items-center rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors ${statusColor}`}
            >
              {statusLabel}
            </span>
          </div>

          <dl className="grid gap-2 text-sm">
            {isTenant3pl && (
              <div className="flex flex-wrap gap-x-2 gap-y-0.5">
                <dt className="text-muted-foreground">{t('table.columns.client', 'Cliente')}</dt>
                <dd className="min-w-0 flex-1 font-medium text-foreground">{entry.team?.name ?? '—'}</dd>
              </div>
            )}
            <div className="flex flex-wrap gap-x-2 gap-y-0.5">
              <dt className="text-muted-foreground">{t('table.columns.estimated_delivery_at', 'Fecha estimada')}</dt>
              <dd className="text-foreground">{formatDate(entry.estimated_delivery_at)}</dd>
            </div>
            <div className="flex flex-wrap gap-x-2 gap-y-0.5">
              <dt className="text-muted-foreground">{t('table.columns.boxes_count', 'Cajas')}</dt>
              <dd className="text-foreground">{entry.boxes_count ?? '—'}</dd>
            </div>
            <div className="flex flex-wrap gap-x-2 gap-y-0.5">
              <dt className="text-muted-foreground">{t('table.columns.tracking_code', 'Código seguimiento')}</dt>
              <dd className="min-w-0 break-all text-foreground">{entry.tracking_code || '—'}</dd>
            </div>
            <div className="flex flex-wrap gap-x-2 gap-y-0.5">
              <dt className="text-muted-foreground">{t('table.columns.created_at', 'Fecha creación')}</dt>
              <dd className="text-foreground">{formatDate(entry.created_at)}</dd>
            </div>
          </dl>
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-9 w-9 shrink-0 p-0"
              aria-label={t('table.actions.menu', 'Abrir menú')}
            >
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLinkItem to={`/merchandise/edit/${entry.id}`} className="cursor-pointer">
              <Eye className="mr-2 h-4 w-4" />
              {t('table.actions.view', 'Ver')}
            </DropdownMenuLinkItem>
            {entry.status === 'PENDIENTE' && canCancel && onCancelEntry && (
              <DropdownMenuItem
                onClick={() => onCancelEntry(entry)}
                className="cursor-pointer text-destructive focus:text-destructive"
              >
                <X className="mr-2 h-4 w-4" />
                {t('table.actions.cancel', 'Cancelar')}
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </article>
  );
};
